import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { journalEntryService } from '../../services/api';

const initialState = {
  journalEntries: [],
  journalEntry: null,
  lookups: null,
  summaryStats: null,
  isLoading: false,
  error: null,
};

export const fetchJournalEntries = createAsyncThunk(
  'journalEntries/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.getJournalEntries(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchJournalEntry = createAsyncThunk(
  'journalEntries/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.getJournalEntry(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createJournalEntry = createAsyncThunk(
  'journalEntries/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.createJournalEntry(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateJournalEntry = createAsyncThunk(
  'journalEntries/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.updateJournalEntry(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteJournalEntry = createAsyncThunk(
  'journalEntries/delete',
  async (id, { rejectWithValue }) => {
    try {
      await journalEntryService.deleteJournalEntry(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const postJournal = createAsyncThunk(
  'journalEntries/post',
  async (id, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.postJournal(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const submitForApproval = createAsyncThunk(
  'journalEntries/submitForApproval',
  async (id, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.submitForApproval(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const approveJournal = createAsyncThunk(
  'journalEntries/approve',
  async (id, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.approveJournal(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rejectJournal = createAsyncThunk(
  'journalEntries/reject',
  async (id, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.rejectJournal(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const reverseJournal = createAsyncThunk(
  'journalEntries/reverse',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.reverseJournal(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchJournalEntryLookups = createAsyncThunk(
  'journalEntries/fetchLookups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await journalEntryService.getLookups();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const journalEntrySlice = createSlice({
  name: 'journalEntries',
  initialState,
  reducers: {
    clearJournalEntryError: (state) => {
      state.error = null;
    },
    clearJournalEntry: (state) => {
      state.journalEntry = null;
    },
  },
  extraReducers: (builder) => {
    // List
    builder
      .addCase(fetchJournalEntries.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchJournalEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both paginated and plain list responses
        state.journalEntries = Array.isArray(action.payload)
          ? action.payload
          : (action.payload.results || []);
      })
      .addCase(fetchJournalEntries.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })

    // Single
      .addCase(fetchJournalEntry.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchJournalEntry.fulfilled, (state, action) => { state.isLoading = false; state.journalEntry = action.payload; })
      .addCase(fetchJournalEntry.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })

    // Create
      .addCase(createJournalEntry.fulfilled, (state, action) => {
        state.journalEntries = [action.payload, ...state.journalEntries];
      })

    // Update
      .addCase(updateJournalEntry.fulfilled, (state, action) => {
        const idx = state.journalEntries.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) state.journalEntries[idx] = action.payload;
        if (state.journalEntry?.id === action.payload.id) state.journalEntry = action.payload;
      })

    // Delete
      .addCase(deleteJournalEntry.fulfilled, (state, action) => {
        state.journalEntries = state.journalEntries.filter(j => j.id !== action.payload);
      })

    // Status transitions: re-fetch triggered in page, just mark loading done
      .addCase(postJournal.fulfilled, (state) => { state.isLoading = false; })
      .addCase(submitForApproval.fulfilled, (state) => { state.isLoading = false; })
      .addCase(approveJournal.fulfilled, (state) => { state.isLoading = false; })
      .addCase(rejectJournal.fulfilled, (state) => { state.isLoading = false; })
      .addCase(reverseJournal.fulfilled, (state, action) => {
        // Add the new reversal journal to the list
        state.journalEntries = [action.payload, ...state.journalEntries];
      })

    // Lookups
      .addCase(fetchJournalEntryLookups.fulfilled, (state, action) => { state.lookups = action.payload; });
  },
});

export const { clearJournalEntryError, clearJournalEntry } = journalEntrySlice.actions;
export default journalEntrySlice.reducer;
